/**
 * 客戶新增彈窗元件 (CustomerAddModal)
 * 
 * 職責：
 * - 提供完整的新增客戶表單 UI
 * - 管理表單內部狀態（頭貼、基本資訊、登入帳號、會員權限、標籤、備註）
 * - 驗證表單資料並呼叫父元件的 onSubmit
 * 
 * @module CustomerAddModal
 */

import { useState, useCallback } from 'react';
import type { CustomerFormData } from '../../../../modules/customer/types';
import { 
  initialNewCustomerForm, 
  levelConfig, 
  discountOptions, 
  quickTagSuggestions,
  defaultAvatars,
  MIN_PASSWORD_LENGTH
} from '../../../../modules/customer/constants';
import { generateRandomPassword, isValidEmail } from '../../../../modules/customer/helpers';

/**
 * CustomerAddModal Props 介面
 */
interface CustomerAddModalProps {
  /** 彈窗是否開啟 */
  isOpen: boolean;
  
  /** 關閉彈窗回調 */
  onClose: () => void;
  
  /** 提交表單回調 */
  onSubmit: (data: CustomerFormData, avatarFile: File | null) => Promise<void>;
  
  /** 是否正在提交中 */
  isSubmitting: boolean;
}

/**
 * 客戶新增彈窗元件
 */
export function CustomerAddModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting
}: CustomerAddModalProps): JSX.Element | null {
  // ==================== 表單狀態 ====================
  
  const [form, setForm] = useState<CustomerFormData>(initialNewCustomerForm);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [showAvatarPicker, setShowAvatarPicker] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [newTag, setNewTag] = useState<string>('');

  // ==================== 表單更新 ====================
  
  const updateForm = useCallback((field: keyof CustomerFormData, value: any): void => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // ==================== 頭貼處理 ====================
  
  const handleAvatarUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatarPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      setShowAvatarPicker(false);
    }
  }, []);

  const selectDefaultAvatar = useCallback((url: string): void => {
    setAvatarPreview(url);
    setAvatarFile(null);
    setShowAvatarPicker(false);
  }, []);

  // ==================== 標籤管理 ====================
  
  const addTag = useCallback((): void => {
    if (newTag.trim() && !form.tags?.includes(newTag.trim())) {
      updateForm('tags', [...(form.tags || []), newTag.trim()]);
      setNewTag('');
    }
  }, [newTag, form.tags, updateForm]);

  const removeTag = useCallback((tagToRemove: string): void => {
    updateForm('tags', form.tags?.filter(tag => tag !== tagToRemove) || []);
  }, [form.tags, updateForm]);

  const addQuickTag = useCallback((tag: string): void => {
    if (!form.tags?.includes(tag)) {
      updateForm('tags', [...(form.tags || []), tag]);
    }
  }, [form.tags, updateForm]);

  // ==================== 密碼生成 ====================
  
  const handleGeneratePassword = useCallback((): void => {
    const newPassword = generateRandomPassword(8);
    updateForm('password', newPassword);
  }, [updateForm]);

  // ==================== 表單驗證與提交 ====================
  
  const handleSubmit = useCallback(async (): Promise<void> => {
    // 驗證必填欄位
    if (!form.name?.trim()) {
      alert('請輸入客戶姓名');
      return;
    }

    if (!form.phone?.trim() && !form.email?.trim()) {
      alert('請至少輸入電話或 Email');
      return;
    }

    if (form.email && !isValidEmail(form.email)) {
      alert('Email 格式不正確');
      return;
    }

    if (form.createAuth) {
      if (!form.email?.trim()) {
        alert('建立登入帳號需要 Email');
        return;
      }
      if (!form.password || form.password.length < MIN_PASSWORD_LENGTH) {
        alert(`密碼長度至少需要 ${MIN_PASSWORD_LENGTH} 個字元`);
        return;
      }
    }

    // 準備提交資料
    // ── Staging 清洗：DB 尚未有 customer_type / identity_number / registered_address 欄位 ──
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customer_type, identity_number, registered_address, ...formWithoutStaging } = form as any;
    const submitData: CustomerFormData = {
      ...formWithoutStaging,
      avatar: avatarPreview || undefined
    };

    await onSubmit(submitData, avatarFile);
    
    // 重置表單
    setForm(initialNewCustomerForm);
    setAvatarFile(null);
    setAvatarPreview('');
    setNewTag('');
  }, [form, avatarPreview, avatarFile, onSubmit]);

  // ==================== 關閉處理 ====================
  
  const handleClose = useCallback((): void => {
    setForm(initialNewCustomerForm);
    setAvatarFile(null);
    setAvatarPreview('');
    setNewTag('');
    setShowAvatarPicker(false);
    onClose();
  }, [onClose]);

  // ==================== 客戶類型處理 ====================

  const customerType = (form as any).customer_type ?? 'individual';

  const isIndividual = customerType === 'individual';
  const isCompany = customerType === 'company';

  // ==================== 渲染 ====================
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-teal-50 to-cyan-50">
          <h2 className="text-xl font-bold text-gray-800">新增客戶</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* 左欄 */}
            <div className="space-y-6">
              {/* 頭貼上傳 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  客戶頭貼
                </label>
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 flex items-center justify-center">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="頭貼預覽"
                        className="w-20 h-20 rounded-full object-cover border-2 border-teal-200"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <i className="ri-user-line text-3xl text-gray-400"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-upload-2-line mr-2"></i>
                      上傳圖片
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-image-line mr-2"></i>
                      選擇預設
                    </button>
                  </div>
                </div>

                {/* 預設頭貼選擇器 */}
                {showAvatarPicker && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 mb-3">選擇預設頭貼：</p>
                    <div className="grid grid-cols-6 gap-3">
                      {defaultAvatars.map((url, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => selectDefaultAvatar(url)}
                          className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 hover:border-teal-500 transition-colors"
                        >
                          <img src={url} alt={`預設頭貼 ${index + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 客戶類型 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  客戶類型
                </label>
                <select
                  value={(form as any).customer_type || 'individual'}
                  onChange={(e) => updateForm('customer_type' as any, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="individual">個人客戶</option>
                  <option value="company">公司客戶</option>
                </select>
              </div>

              {/* 基本資訊 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  客戶姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name || ''}
                  onChange={(e) => updateForm('name', e.target.value)}
                  placeholder="請輸入客戶姓名"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 身分證字號（個人客戶）— staging */}
              {isIndividual && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    身分證字號
                    <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">Staging</span>
                  </label>
                  <input
                    type="text"
                    value={(form as any).identity_number || ''}
                    onChange={(e) => updateForm('identity_number' as any, e.target.value)}
                    placeholder="請輸入身分證字號（A123456789）"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <p className="mt-1 text-xs text-amber-600">
                    <i className="ri-information-line mr-1"></i>
                    此欄位為 staging，待 customer identity schema migration 完成後啟用
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  公司名稱
                </label>
                <input
                  type="text"
                  value={form.company || ''}
                  onChange={(e) => updateForm('company', e.target.value)}
                  placeholder="請輸入公司名稱"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  聯絡電話
                </label>
                <input
                  type="tel"
                  value={form.phone || ''}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="請輸入聯絡電話"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => updateForm('email', e.target.value)}
                  placeholder="請輸入 Email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              {/* LINE ID */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-line-fill text-green-500 mr-1"></i>
                  LINE ID
                </label>
                <input
                  type="text"
                  value={form.line_id || ''}
                  onChange={(e) => updateForm('line_id', e.target.value)}
                  placeholder="請輸入客戶的 LINE ID"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  用於 LINE 通知功能，可在訂單狀態變更時發送通知
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  統一編號{isIndividual ? '（公司用）' : ''}
                </label>
                <input
                  type="text"
                  value={form.tax_id || ''}
                  onChange={(e) => updateForm('tax_id', e.target.value)}
                  placeholder={isCompany ? "請輸入統一編號" : "公司客戶請填寫"}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  地址
                </label>
                <input
                  type="text"
                  value={form.address || ''}
                  onChange={(e) => updateForm('address', e.target.value)}
                  placeholder="請輸入地址"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              {/* 戶籍地址（個人客戶）— staging */}
              {isIndividual && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    戶籍地址
                    <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">Staging</span>
                  </label>
                  <input
                    type="text"
                    value={(form as any).registered_address || ''}
                    onChange={(e) => updateForm('registered_address' as any, e.target.value)}
                    placeholder="請輸入戶籍地址（若與聯絡地址不同）"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <p className="mt-1 text-xs text-amber-600">
                    <i className="ri-information-line mr-1"></i>
                    此欄位為 staging，待 customer identity schema migration 完成後啟用
                  </p>
                </div>
              )}
            </div>

            {/* 右欄 */}
            <div className="space-y-6">
              {/* 前台登入帳號設定 */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="createAuth"
                    checked={form.createAuth || false}
                    onChange={(e) => updateForm('createAuth', e.target.checked)}
                    className="w-4 h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="createAuth" className="text-sm font-semibold text-gray-700 cursor-pointer">
                    同時建立前台登入帳號
                  </label>
                </div>

                {form.createAuth && (
                  <div className="space-y-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        登入密碼
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={form.password || ''}
                          onChange={(e) => updateForm('password', e.target.value)}
                          placeholder="請輸入密碼"
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      自動產生密碼
                    </button>
                  </div>
                )}
              </div>

              {/* 會員權限 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  會員類別
                </label>
                <select
                  value={form.member_level || 'normal'}
                  onChange={(e) => updateForm('member_level', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  {Object.entries(levelConfig).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.icon} {config.label} - {config.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  會員折扣
                </label>
                <select
                  value={form.discount || 100}
                  onChange={(e) => updateForm('discount', Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  {discountOptions.map((discount) => (
                    <option key={discount} value={discount}>
                      {discount}折 {discount < 100 ? `(打 ${100 - discount}% 折扣)` : '(無折扣)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* 標籤管理 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  客戶標籤
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    placeholder="輸入標籤名稱"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>

                {/* 已選標籤 */}
                {form.tags && form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {form.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 text-sm rounded-full"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="w-4 h-4 flex items-center justify-center hover:bg-teal-200 rounded-full transition-colors"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* 快速標籤 */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">快速新增：</p>
                  <div className="flex flex-wrap gap-2">
                    {quickTagSuggestions.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addQuickTag(tag)}
                        disabled={form.tags?.includes(tag)}
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 備註 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  備註
                </label>
                <textarea
                  value={form.notes || ''}
                  onChange={(e) => updateForm('notes', e.target.value)}
                  placeholder="輸入備註資訊"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                />
              </div>

              {/* Urent 客戶勾選 */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isUrent"
                    checked={form.is_urent || false}
                    onChange={(e) => updateForm('is_urent', e.target.checked)}
                    className="w-5 h-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500 cursor-pointer"
                  />
                  <label htmlFor="isUrent" className="cursor-pointer">
                    <span className="text-sm font-semibold text-gray-700">Urent 客戶</span>
                    <p className="text-xs text-gray-500 mt-0.5">勾選後此客戶將納入 Urent 管理系統</p>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                儲存中...
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                儲存客戶
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
